import type { FiltroDeComprobantes, TipoDeComprobante } from '../api';

/**
 * El estado de los filtros del listado (KAN-146), y su traducción al filtro que
 * declara el contrato.
 *
 * Vive fuera del componente para poder probar las reglas que importan —cuándo
 * se puede consultar, cuándo se vuelve a la primera página— sin montar nada.
 */

/**
 * Comprobantes por página. **20 y no 200** (el máximo que acepta el backend):
 * esto se lee en pantalla, no se exporta. Traer diez veces más filas para que
 * nadie las mire cuesta base de datos y red en cada cambio de filtro.
 */
export const TAMANO_DE_PAGINA = 20;

/** Los filtros tal como los maneja la pantalla: `''`/`null` significan "sin filtrar". */
export interface FiltroLocal {
  /** `null` hasta que se elige un contribuyente. Sin él **no se consulta nada**. */
  tenantId: number | null;
  /** `aaaa-mm-dd`, inclusive. `''` = desde el principio. */
  desde: string;
  /** `aaaa-mm-dd`, inclusive. `''` = hasta hoy. */
  hasta: string;
  /** `CbteTipo`, o `null` para todos. */
  tipo: TipoDeComprobante | null;
  /** Página **0-based**, igual que el contrato. */
  pagina: number;
}

/**
 * Arranca **sin rango de fechas**, a propósito.
 *
 * La tentación es preseleccionar "este mes", pero acá eso escondería la mayor
 * parte de la facturación detrás de un filtro que nadie puso, y esta pantalla
 * existe justamente para que alguien pueda encontrar un comprobante viejo (por
 * ejemplo el que la reconciliación marcó como faltante). El listado ya viene
 * del más reciente al más viejo: lo primero que se ve es lo último que se
 * emitió, sin ocultar el resto.
 */
export const FILTRO_INICIAL: FiltroLocal = {
  tenantId: null,
  desde: '',
  hasta: '',
  tipo: null,
  pagina: 0,
};

/**
 * `true` si el rango está al revés (`desde` posterior a `hasta`).
 *
 * Compara los strings directamente: en formato `aaaa-mm-dd` el orden
 * alfabético **es** el cronológico, y así no hay que construir un `Date` —que
 * en un date-only ISO se interpreta como medianoche UTC y corre un día en la
 * Argentina (ver `fechaArgentina` en `dominio.ts`).
 */
export function rangoInvertido(filtro: FiltroLocal): boolean {
  return filtro.desde !== '' && filtro.hasta !== '' && filtro.desde > filtro.hasta;
}

/**
 * `true` si tiene sentido pedirle algo al backend: hay tenant elegido y el
 * rango no está invertido.
 *
 * El rango invertido se ataja acá **además** de en el backend (que contesta
 * `400`): mientras alguien tipea un rango a mano pasa por estados inválidos, y
 * cada uno sería un pedido destinado a fallar y un cartel rojo parpadeando.
 */
export function puedeConsultar(filtro: FiltroLocal): boolean {
  return filtro.tenantId !== null && !rangoInvertido(filtro);
}

/**
 * Aplica un cambio de filtro **y vuelve a la primera página**.
 *
 * Lo segundo no es un detalle: cambiar de tipo estando en la página 3 pediría
 * la página 3 del resultado nuevo, y "una página más allá del final viene
 * vacía" — que en esta pantalla se lee como *este contribuyente no facturó
 * nada*, que es una afirmación falsa sobre un registro fiscal. Por eso el
 * número de página no se puede cambiar por esta función: para eso está
 * {@link enPagina}.
 */
export function conCambio(
  filtro: FiltroLocal,
  cambios: Partial<Omit<FiltroLocal, 'pagina'>>,
): FiltroLocal {
  return { ...filtro, ...cambios, pagina: 0 };
}

/** Mueve de página sin tocar el resto del filtro. */
export function enPagina(filtro: FiltroLocal, pagina: number): FiltroLocal {
  return { ...filtro, pagina };
}

/**
 * Traduce al filtro del contrato. Lo vacío **se omite** en vez de mandarse
 * vacío: el backend distingue "sin filtro" de "filtro vacío", y `?desde=` no es
 * lo mismo que no mandar `desde`.
 *
 * Cuando todavía no hay tenant elegido devuelve `tenantId: 0`, que **nunca sale
 * a la red**: la query está deshabilitada ({@link puedeConsultar}). Se necesita
 * un valor estable igual porque el filtro entero forma parte de la clave de
 * caché.
 */
export function aFiltroDeApi(filtro: FiltroLocal): FiltroDeComprobantes {
  return {
    tenantId: filtro.tenantId ?? 0,
    ...(filtro.desde === '' ? {} : { desde: filtro.desde }),
    ...(filtro.hasta === '' ? {} : { hasta: filtro.hasta }),
    ...(filtro.tipo === null ? {} : { tipo: filtro.tipo }),
    pagina: filtro.pagina,
    tamano: TAMANO_DE_PAGINA,
  };
}
